import Linkify from 'linkify-react';
import React from 'react';
import { MdVerified } from 'react-icons/md';
import styled, { css } from 'styled-components';

import useApi from '../../actions/useApi';
import { useAppContext } from '../../appContext';
import ChatDarkActiveIcon from '../../assets/chat-dark-active.svg';
import ChatLightActiveIcon from '../../assets/chat-light-active.svg';
import ChatLightIcon from '../../assets/chat-light.svg';
import ChatIcon from '../../assets/chat.svg';
import HeartDarkActiveIcon from '../../assets/heart-active-dark.svg';
import HeartLightActiveIcon from '../../assets/heart-active-light.svg';
import HeartDarkIcon from '../../assets/heart-dark.svg';
import HeartIcon from '../../assets/heart.svg';
import useUserSession from '../../hooks/useUserSession';
import { openUserForm, selectComment } from '../../store/actions';
import { useStateContext } from '../../store/stateContext';
import { formatName } from '../../utils';
import Avatar from '../avatar';
import { FaviconImage } from '../selected-comment';

import type { PollOption, Comment } from '../../types';
interface CommentCardProps {
    comment: Comment
    background?: string
    shadow?: boolean;
    openMode?: boolean;
    additionalClasses?: string;
    fullWidth?: boolean;
    isPrimaryComment?: boolean;
}

const CommentContainer = styled.div<{
    isDarkMode: boolean;
    background: string;
    shadow: boolean;
    additionalClasses?: string;
    fullWidth?: boolean;
}>`
    background-color: ${props => props.isDarkMode
        ? (props.background === 'white' ? 'white' : props.background)
        : props.background} !important;
    color: ${props => props.isDarkMode
        ? '#222222'
        : 'white'} !important;
    border-radius: 16px !important; /* 1rem = 16px */
    padding: 16px !important; /* 1rem = 16px */
    cursor: pointer;
    width: ${props => props.fullWidth ? '100%' : '360px'} !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
    box-shadow: ${props => props.shadow ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none'} !important;
    ${props => props.additionalClasses && props.additionalClasses};
    * {
        box-sizing: border-box !important;
    }
    
    @media (max-width: 768px) {
        width: 100% !important;
        min-width: 100% !important;
    }
`;

const CommentContent = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    gap: 8px !important; /* 0.5rem = 8px */
`;

const AvatarContainer = styled.div`
    width: 36px !important;
`;

const ContentContainer = styled.div`
    @media (max-width: 768px) {
        max-width: 90% !important;
    }
    max-width: 264px !important;
    width: 100%;
`;

const UserName = styled.p<{ isDarkMode: boolean }>`
    all: revert;
    font-size: 12px !important;
    font-weight: 700;
    color: ${props => props.isDarkMode ? 'black' : '#999999'};
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 6.4px !important; /* 0.4rem = 6.4px */
    margin: 0px !important;
    line-height: 16px !important;
    letter-spacing: 0px !important;
`;

const MessageText = styled.p<{ isDarkMode: boolean }>`
    all: revert;
    font-size: 14px !important;
    line-height: 20px !important;
    margin-bottom: 4px !important; /* 0.25rem = 4px */
    font-weight: 400;
    margin-top: 2px !important;
    letter-spacing: 0px !important;
    color: ${props => props.isDarkMode ? 'black' : 'white'};
`;

const InteractionContainer = styled.div`
    display: flex;
    color: #999999;
    margin-top: 8px !important; /* 0.5rem = 8px */
    height: 20px !important;
`;

const LikeContainer = styled.div<{
    isActive: boolean;
    isDarkMode: boolean;
    animate: boolean;
    openMode: boolean;
}>`
    display: flex;
    align-items: center;
    margin-right: 16px !important; /* 1rem = 16px */
    gap: 4px !important; /* 0.25rem = 4px */
    color: ${props => props.isActive && props.openMode
        ? (props.isDarkMode ? '#5942D1' : '#FF82EB')
        : '#999999'};
    
    ${props => props.animate && css`
        animation: ${props.isDarkMode ? 'like-animation-dark' : 'like-animation'} 0.3s ease-in-out !important;
    `}
`;

const CommentIconContainer = styled.div<{
    isActive: boolean;
    isDarkMode: boolean;
    openMode: boolean;
}>`
    display: flex;
    align-items: center;
    gap: 4px !important; /* 0.25rem = 4px */
    color: ${props => props.isActive && props.openMode
        ? (props.isDarkMode ? '#5942D1' : '#78EBD6')
        : '#999999'};
`;

const InteractionText = styled.span<{
    isActive: boolean;
    isDarkMode: boolean;
    type: 'like' | 'comment';
}>`
    all: revert;
    font-size: 12px !important;
    color: ${props => props.isActive
        ? (props.isDarkMode
            ? '#5942D1'
            : props.type === 'like' ? '#FF82EB' : '#78EBD6')
        : '#999999'};
`;

const PollOptionsContainer = styled.div`
max-width: 264px;
    display: flex;
    flex-direction: column;
    gap: 8px !important; /* 0.5rem = 8px */
    margin-top: 8px !important; /* 0.5rem = 8px */
    margin-left: -16px !important; /* -1rem = -16px */
    width: calc(100% + 16px) !important; /* 1rem = 16px */
    max-width: calc(100% + 16px) !important; /* 1rem = 16px */
    overflow: hidden !important;
    margin-bottom: 8px !important; /* 0.5rem = 8px */
`;

const StyledPollOption = styled.div<{ isDarkMode: boolean, userVoted: boolean }>`
    position: relative;
    padding: 6px 16px !important; /* 0.375rem = 6px, 1rem = 16px */
    border-radius: 9999px !important;
    background-color: ${props => props.isDarkMode ? '#DADADA' : 'white'} !important;
    color: ${props => props.isDarkMode
        ? (props.userVoted ? 'white' : 'black')
        : '#4A4A4A'} !important;
    cursor: ${props => props.userVoted ? 'default' : 'pointer'} !important;
    height: 28px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    font-size: 12px !important;
`;

const PollBar = styled.div<{ percentage: number, isDarkMode: boolean }>`
    position: absolute;
    top: 0;
    left: 0;
    height: 100% !important;
    background-color: ${props => props.isDarkMode ? '#666666' : '#DADADA'} !important;
    border-radius: 9999px !important;
    border-top-right-radius: ${props => props.percentage > 95 ? '9999px' : '0px'} !important;
    border-bottom-right-radius: ${props => props.percentage > 95 ? '9999px' : '0px'} !important;
    width: ${props => `${props.percentage}%`} !important;
`;

const OptionContent = styled.div`
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100% !important;
    overflow: hidden !important;

    span {
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
    }
`;

const PercentageText = styled.span`
    color: #A0A0A0 !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
    font-size: 12px !important;
`;

const ResponseLink = styled.a`
    color: #23C68D !important;
    font-size: 14px !important;
    margin-left: 16px !important; /* 1rem = 16px */
    text-decoration: none !important;
    width: 100% !important;
    text-align: left !important;
`;

const CommentCard: React.FC<CommentCardProps> = ({ additionalClasses, comment, background = '#585568', shadow = true, openMode = false, fullWidth = false, isPrimaryComment = false }) => {
    const { likeComment, unlikeComment, pollVote } = useApi();
    const { clearTimers, openCommentsList } = useAppContext();
    const [animate, setAnimate] = React.useState(false);
    const { id, user: { name, image, role_id }, message, like_count, replies, message_type_id, poll_options = [], user_voted = false, total_votes = 0 } = comment;
    const { state, dispatch } = useStateContext();
    const { isLoggedIn } = useUserSession();
    const [selectedOptionId, setSelectedOptionId] = React.useState<number | null>(null);
    const isDarkMode = state.themeMode === 'dark';
    const isPoll = message_type_id === 3;

    const handleClick = (evt: React.MouseEvent<HTMLDivElement>) => {
        if (isPoll && !isPrimaryComment) {
            if (!user_voted) {
                evt.stopPropagation();
                return;
            }
        }
        dispatch(selectComment({ commentId: id }))
        clearTimers();
        openCommentsList();
    }

    const handleLike = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (comment.approved === null) {
            return
        }
        clearTimers();
        if (comment.liked) {
            unlikeComment(`${state.productInfo?.id}`, comment.id);
        } else {
            likeComment(`${state.productInfo?.id}`, comment.id);
        }

        // If this is a primary comment, also open the comment details
        if (isPrimaryComment) {
            dispatch(selectComment({ commentId: id }));
            openCommentsList();
        }

        setAnimate(true);
        setTimeout(() => setAnimate(false), 300);
    };

    const componentDecorator = (href: string, text: string, key: number) => (
        <a
            href={href}
            key={key}
            target="_blank"
            rel="noopener noreferrer"
            style={{
                textDecoration: 'underline',
                color: isDarkMode ? '#5942D1' : '#23C68D'
            }}
        >
            {text.slice(0, 20)}...
        </a>
    );

    const renderPollOptions = () => (
        <PollOptionsContainer>
            {poll_options.map((option: PollOption) => {
                const percentage = (option.vote_count / total_votes) * 100;
                return (
                    <StyledPollOption
                        key={option.id}
                        isDarkMode={isDarkMode}
                        userVoted={user_voted}
                        onClick={(evt: React.MouseEvent<HTMLDivElement>) => {
                            if (!user_voted) {
                                !isPrimaryComment && evt.stopPropagation();
                                handlePollVote(option.id)
                            }
                        }}
                    >
                        {user_voted && (
                            <PollBar
                                percentage={percentage}
                                isDarkMode={isDarkMode}
                            />
                        )}
                        <OptionContent>
                            <span>{option.option_text}</span>
                            {user_voted && <PercentageText>{percentage.toFixed(0)}%</PercentageText>}
                        </OptionContent>
                    </StyledPollOption>
                );
            })}
            {user_voted && <ResponseLink href="#">{openMode ? `${total_votes} responses` : 'See responses'}</ResponseLink>}
        </PollOptionsContainer>
    );
    console.log('selectedOption', { selectedOptionId })
    const handlePollVote = (optionId: number) => {
        if (isPrimaryComment) {
            console.log(optionId, id)
            dispatch(selectComment({ commentId: id }))
            console.log('after select')
        }
        if (!isLoggedIn) {
            setSelectedOptionId(optionId);
            dispatch(openUserForm({
                commentText: message,
                label: "Cast my vote",
                additionalPayload: {
                    selectedOptionId: optionId
                },
                onSuccess: (values, additionalPayload) => {
                    if (additionalPayload.selectedOptionId !== null) {
                        pollVote(`${state.productInfo?.id}`, comment.id, additionalPayload.selectedOptionId as number, values);
                        setSelectedOptionId(null);
                    }
                },
                onClose: () => {
                    setSelectedOptionId(null);
                }
            }));
            return;
        }
        pollVote(`${state.productInfo?.id}`, comment.id, optionId);
    };

    return (
        <CommentContainer
            isDarkMode={isDarkMode}
            background={background}
            shadow={shadow}
            additionalClasses={additionalClasses}
            fullWidth={fullWidth}
            className="comment-container"
        >
            <CommentContent onClick={handleClick}>
                <AvatarContainer>
                    {role_id === 3 ? (
                        <FaviconImage
                            alt="favicon"
                            src={`https://www.google.com/s2/favicons?domain=https://${window.location.hostname}&sz=64`}
                            size={32}
                        />
                    ) : (
                        <Avatar url={image} name={name} dimensionClasses='w-[32px] h-[32px]' />
                    )}
                </AvatarContainer>
                <ContentContainer>
                    <UserName isDarkMode={isDarkMode}>
                        {formatName(name)}
                        {role_id === 3 && <MdVerified size={16} color={isDarkMode ? '#5942D1' : '#23C68D'} />}
                    </UserName>
                    <Linkify componentDecorator={componentDecorator}>
                        <MessageText isDarkMode={isDarkMode}>{message}</MessageText>
                    </Linkify>

                    {isPoll && renderPollOptions()}

                    {!isPoll && (
                        <InteractionContainer>
                            <LikeContainer
                                isActive={comment.liked && like_count > 0}
                                isDarkMode={isDarkMode}
                                animate={animate}
                                openMode={openMode}
                                onClick={handleLike}
                            >
                                <img
                                    src={comment.liked
                                        ? (isDarkMode ? HeartDarkActiveIcon : HeartLightActiveIcon)
                                        : (isDarkMode ? HeartDarkIcon : HeartIcon)
                                    }
                                    alt="Like"
                                    style={{ height: '18px' }}
                                />
                                <InteractionText
                                    isActive={comment.liked && like_count > 0}
                                    isDarkMode={isDarkMode}
                                    type="like"
                                >
                                    {like_count}
                                </InteractionText>
                            </LikeContainer>

                            <CommentIconContainer
                                isActive={replies.length > 0}
                                isDarkMode={isDarkMode}
                                openMode={openMode}
                            >
                                <img
                                    src={openMode && replies.length
                                        ? (isDarkMode ? ChatDarkActiveIcon : ChatLightActiveIcon)
                                        : (isDarkMode ? ChatIcon : ChatLightIcon)
                                    }
                                    alt="Comments"
                                />
                                <InteractionText
                                    isActive={openMode && replies.length > 0}
                                    isDarkMode={isDarkMode}
                                    type="comment"
                                >
                                    {replies.length}
                                </InteractionText>
                            </CommentIconContainer>
                        </InteractionContainer>
                    )}
                </ContentContainer>
            </CommentContent>
        </CommentContainer>
    );
};

export default CommentCard;
